import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { Brain, TrendingUp, TrendingDown, Minus, Sparkles, RefreshCw, Lightbulb, Target } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrendIcon = ({ trend }) => {
  switch (trend) {
    case "improving":
      return <TrendingUp className="w-5 h-5 text-neon-green" />;
    case "declining":
      return <TrendingDown className="w-5 h-5 text-neon-red" />;
    default:
      return <Minus className="w-5 h-5 text-neon-yellow" />;
  }
};

const TrendLabel = ({ trend }) => {
  const labels = {
    improving: { text: "Mejorando", color: "text-neon-green" },
    declining: { text: "Decayendo", color: "text-neon-red" },
    stable: { text: "Estable", color: "text-neon-yellow" },
  };
  const { text, color } = labels[trend] || labels.stable;
  return <span className={`font-rajdhani font-bold ${color}`}>{text}</span>;
};

export const AIInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/insights`);
      setInsights(response.data);
    } catch (err) {
      console.error("Error fetching insights:", err);
      setError(err.response?.data?.detail || "Error al obtener insights de IA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  return (
    <GlassCard className="p-6" neon>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-neon-purple/20 border border-neon-purple/30">
            <Brain className="w-6 h-6 text-neon-purple" />
          </div>
          <div>
            <h2 className="heading-cyber text-xl">Análisis IA</h2>
            <p className="text-xs text-slate-400 font-rajdhani">Powered by Gemini 3 Flash</p>
          </div>
        </div>
        <Button
          onClick={fetchInsights}
          disabled={loading}
          data-testid="refresh-insights-btn"
          className="cyber-button-purple px-3 py-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-neon-purple/30 border-t-neon-purple rounded-full animate-spin" />
            <Sparkles className="w-6 h-6 text-neon-purple absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-4 text-slate-400 font-rajdhani">Analizando tus patrones emocionales...</p>
        </div>
      )}

      {error && !loading && (
        <div className="text-center py-8">
          <p className="text-neon-red font-inter">{error}</p>
          <Button onClick={fetchInsights} className="mt-4 cyber-button">
            Reintentar
          </Button>
        </div>
      )}

      {insights && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          {/* Summary and Trend */}
          <div className="bg-black/30 rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <TrendIcon trend={insights.mood_trend} />
              <span className="text-slate-400 font-rajdhani text-sm">Tendencia:</span>
              <TrendLabel trend={insights.mood_trend} />
            </div>
            <p className="text-slate-300 font-inter leading-relaxed" data-testid="ai-summary">
              {insights.summary}
            </p>
          </div>

          {/* Patterns Detected */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-neon-cyan" />
              <h3 className="font-rajdhani font-bold text-sm uppercase tracking-wide text-slate-300">
                Patrones Detectados
              </h3>
            </div>
            <ul className="space-y-2" data-testid="ai-patterns">
              {insights.patterns.map((pattern, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-2 text-sm"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan mt-2 flex-shrink-0" />
                  <span className="text-slate-400 font-inter">{pattern}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Recommendations */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-neon-yellow" />
              <h3 className="font-rajdhani font-bold text-sm uppercase tracking-wide text-slate-300">
                Recomendaciones
              </h3>
            </div>
            <ul className="space-y-2" data-testid="ai-recommendations">
              {insights.recommendations.map((rec, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-2 text-sm"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-yellow mt-2 flex-shrink-0" />
                  <span className="text-slate-400 font-inter">{rec}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      )}
    </GlassCard>
  );
};
